import React, { useEffect, useState } from 'react'
import { CModal, CModalHeader, CModalTitle, CModalBody, CModalFooter } from '@coreui/react'
import { wtApiCall } from 'src/helpers/commons/ApiWrapper'
import { lzNotifyErrorMessageFromResult, lzNotifySuccessMessage } from 'src/helpers/commons/notifications'
import { wtGetFromLocalStorage } from 'src/helpers/commons/LocalStorage'

const GREEN = '#1F4D3A'
const RED = '#b42318'
const fmtDay = (v) =>
  v ? new Date(v).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : ''
const personName = (u) => (u ? `${u.first_name || ''} ${u.last_name || ''}`.trim() : '')

// Shown to poster, partner and back office (backend hides it from others), for 30 days
export function CorrectionNotice({ p }) {
  if (!p.deal_corrected_at) return null
  if (Date.now() - new Date(p.deal_corrected_at).getTime() > 30 * 86400000) return null
  const reversed = p.deal_corrected_type === 'reversed'
  return (
    <div
      style={{
        background: '#fff8ec', border: '1px solid #f0d9ad', borderRadius: 8,
        padding: '10px 12px', margin: '12px 0', fontSize: 14,
      }}
    >
      <strong>{reversed ? 'Deal reversed by KLS' : 'Deal details corrected by KLS'}</strong>
      <span style={{ color: '#666' }}> · {fmtDay(p.deal_corrected_at)}</span>
      {p.deal_corrected_reason && <div style={{ marginTop: 4 }}>Reason: {p.deal_corrected_reason}</div>}
    </div>
  )
}

export default function CorrectDeal({ id, p, reload }) {
  const me = wtGetFromLocalStorage('authUser') || {}
  const isTopLevel = ['Super Admin', 'Manager'].includes(me.role_name)
  const isDeal = p.status === 'Sold' || p.status === 'Rented'

  const [open, setOpen] = useState(false)
  const [mode, setMode] = useState('edit')
  const [price, setPrice] = useState('')
  const [date, setDate] = useState('')
  const [source, setSource] = useState('')
  const [partnerId, setPartnerId] = useState('')
  const [agents, setAgents] = useState(null)
  const [search, setSearch] = useState('')
  const [reason, setReason] = useState('')
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)

  const isRent = p.status === 'Rented'
  const who = isRent ? 'tenant' : 'buyer'

  const start = () => {
    setMode('edit')
    setPrice(p.deal_price ?? '')
    setDate(p.deal_date ? String(p.deal_date).slice(0, 10) : '')
    setSource(p.deal_source || '')
    setPartnerId(p.deal_partner_id ? String(p.deal_partner_id) : '')
    setSearch('')
    setReason('')
    setError('')
    setOpen(true)
  }

  // Agent list for "Another KLS agent" (same endpoint as Mark sold)
  useEffect(() => {
    if (!open || agents !== null) return
    wtApiCall(
      'deal-agents?property_id=' + id,
      {},
      'get',
      (res) => {
        const d = res.data
        const list = Array.isArray(d) ? d : d?.agents || d?.data || []
        setAgents(
          list
            .map((a) => ({
              id: String(a.value ?? a.id),
              label: a.label ?? a.name ?? personName(a),
            }))
            .filter((a) => a.id !== String(p.created_by_id)),
        )
      },
      () => setAgents([]),
    )
  }, [open])

  if (!isTopLevel || !isDeal) return null

  const submit = () => {
    if (!reason.trim()) {
      setError('Please give a reason for the correction.')
      return
    }
    if (mode === 'edit' && source === 'kls' && !partnerId) {
      setError('Please pick the KLS agent who brought the client.')
      return
    }
    setBusy(true)
    const body =
      mode === 'reverse'
        ? { action: 'reverse', reason: reason.trim() }
        : {
            action: 'edit',
            reason: reason.trim(),
            deal_price: price === '' ? '' : price,
            deal_date: date,
            deal_source: source,
            deal_partner_id: source === 'kls' ? partnerId : '',
          }
    wtApiCall(
      'property/' + id + '/deal-correct',
      body,
      'post',
      (res) => {
        setBusy(false)
        setOpen(false)
        lzNotifySuccessMessage(res.message || (mode === 'reverse' ? 'Deal reversed.' : 'Deal details corrected.'))
        reload()
      },
      (err) => {
        setBusy(false)
        lzNotifyErrorMessageFromResult(err)
      },
    )
  }

  const q = search.trim().toLowerCase()
  const hits = (agents || []).filter((a) => !q || a.label.toLowerCase().includes(q)).slice(0, 50)
  const currentPartner = personName(p.deal_partner)

  const pill = (key, label) => (
    <button
      type="button"
      onClick={() => { setMode(key); setError('') }}
      style={{
        flex: 1, padding: '8px 10px', borderRadius: 6, fontWeight: 600,
        border: '1px solid ' + (mode === key ? GREEN : '#d9d9d2'),
        background: mode === key ? GREEN : '#fff', color: mode === key ? '#fff' : '#333',
      }}
    >
      {label}
    </button>
  )

  return (
    <>
      <button
        type="button"
        onClick={start}
        style={{
          width: '100%', margin: '10px 0', padding: '9px 12px', borderRadius: 8,
          border: '1px solid #d9d9d2', background: '#fff', fontWeight: 600, color: GREEN,
        }}
      >
        Correct deal
      </button>

      <CModal visible={open} onClose={() => setOpen(false)} alignment="center">
        <CModalHeader>
          <CModalTitle>Correct deal</CModalTitle>
        </CModalHeader>
        <CModalBody>
          <div style={{ display: 'flex', gap: 8, marginBottom: 14 }}>
            {pill('edit', 'Edit deal details')}
            {pill('reverse', 'Reverse deal')}
          </div>

          {mode === 'edit' ? (
            <>
              <label className="form-label fw-semibold">Final price ({p.currency || 'KES'})</label>
              <input
                type="number"
                min="0"
                className="form-control mb-3"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder="Not recorded"
              />

              <label className="form-label fw-semibold">Date {isRent ? 'rented' : 'sold'}</label>
              <input type="date" className="form-control mb-3" value={date} onChange={(e) => setDate(e.target.value)} />

              <label className="form-label fw-semibold">Who introduced the {who}?</label>
              <select className="form-select mb-3" value={source} onChange={(e) => setSource(e.target.value)}>
                <option value="">Choose…</option>
                <option value="own">The listing agent's own client</option>
                <option value="kls">Another KLS agent</option>
                <option value="outside">Outside KLS</option>
              </select>

              {source === 'kls' && (
                <>
                  {currentPartner && (
                    <div className="small text-muted mb-1">Currently: {currentPartner}</div>
                  )}
                  <input
                    type="text"
                    className="form-control mb-2"
                    placeholder="Search agent"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                  <select
                    className="form-select mb-2"
                    size={Math.min(6, Math.max(2, hits.length))}
                    value={partnerId}
                    onChange={(e) => setPartnerId(e.target.value)}
                  >
                    {agents === null && <option disabled>Loading…</option>}
                    {hits.map((a) => (
                      <option key={a.id} value={a.id}>{a.label}</option>
                    ))}
                  </select>
                  <div className="small text-muted mb-3">
                    If the agent changes, the new agent will be asked to confirm. The previous agent's
                    confirmation and private commission are removed.
                  </div>
                </>
              )}
              <div className="small text-muted mb-3">
                The listing agent's own commission is not changed.
              </div>
            </>
          ) : (
            <p style={{ marginBottom: 14 }}>
              The listing returns to <strong>{isRent ? 'For Rent' : 'For Sale'}</strong> and is live again
              straight away. All deal details are removed, including commissions and partner confirmation.
            </p>
          )}

          <label className="form-label fw-semibold">Reason (required)</label>
          <textarea
            className="form-control"
            rows={3}
            maxLength={500}
            value={reason}
            onChange={(e) => { setReason(e.target.value); setError('') }}
            placeholder="This will be shown to the listing agent."
          />
          {error && <div className="text-danger small mt-1">{error}</div>}
        </CModalBody>
        <CModalFooter>
          <button type="button" className="btn btn-outline-secondary" onClick={() => setOpen(false)}>
            Cancel
          </button>
          <button
            type="button"
            className="btn text-white"
            disabled={busy}
            onClick={submit}
            style={{ background: mode === 'reverse' ? RED : GREEN, border: 'none' }}
          >
            {busy ? 'Saving…' : mode === 'reverse' ? 'Reverse deal' : 'Save correction'}
          </button>
        </CModalFooter>
      </CModal>
    </>
  )
}